import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { StatusChip } from 'src/components/payments-ops/StatusChip'
import { SectionCard } from 'src/components/shared/SectionCard'
import type { ReconciliationRun } from 'src/interfaces/reconciliation'
import { addS } from 'src/utils/baseUtils'
import { usePartnerNameMap } from 'src/utils/hooks/paymentsOps/usePartnerNameMap'
import { resolvePartnerName } from 'src/utils/paymentsOps/partnerNames'

interface Props {
  run: ReconciliationRun
}

const ReconciliationResultCardComponent = ({ run }: Props) => {
  const nameMap = usePartnerNameMap()
  const { errors, warnings } = run.result
  const hasIssues = errors.length > 0 || warnings.length > 0

  const rawJson = useMemo(() => {
    return JSON.stringify(run.result, null, 2)
  }, [run.result])

  return (
    <SectionCard
      title={`Result — ${resolvePartnerName(nameMap, run.request.partnerId)} · ${
        run.request.startDate
      } → ${run.request.endDate}`}
      actions={
        hasIssues ? (
          <div css={tw`flex flex-row gap-2`}>
            <StatusChip
              label={`${errors.length} ${addS(errors.length, 'error')}`}
              tone={errors.length > 0 ? 'critical' : 'neutral'}
            />
            <StatusChip
              label={`${warnings.length} ${addS(warnings.length, 'warning')}`}
              tone={warnings.length > 0 ? 'warning' : 'neutral'}
            />
          </div>
        ) : (
          <StatusChip label="No issues found" tone="success" />
        )
      }
    >
      <div css={tw`flex flex-col gap-4`} data-testid="reconciliation-result">
        {errors.length > 0 ? (
          <div css={tw`flex flex-col gap-1`}>
            <Typography variant="h6" css={tw`text-critical-500`}>
              Errors
            </Typography>
            <ul css={tw`m-0 pl-5`}>
              {errors.map((line, index) => {
                return (
                  <li key={`error-${index}`}>
                    <Typography variant="body2">{line}</Typography>
                  </li>
                )
              })}
            </ul>
          </div>
        ) : null}
        {warnings.length > 0 ? (
          <div css={tw`flex flex-col gap-1`}>
            <Typography variant="h6" css={tw`text-warning-700`}>
              Warnings
            </Typography>
            <ul css={tw`m-0 pl-5`}>
              {warnings.map((line, index) => {
                return (
                  <li key={`warning-${index}`}>
                    <Typography variant="body2">{line}</Typography>
                  </li>
                )
              })}
            </ul>
          </div>
        ) : null}
        {hasIssues ? null : (
          <Typography variant="body2">Everything reconciled cleanly for this range.</Typography>
        )}
        <details>
          <summary css={tw`cursor-pointer text-sm text-shades-500`}>Raw response</summary>
          <pre css={tw`mt-2 overflow-x-auto rounded-lg bg-shades-50 p-3 text-xs`}>{rawJson}</pre>
        </details>
      </div>
    </SectionCard>
  )
}

export const ReconciliationResultCard = memo(ReconciliationResultCardComponent)
